'use client';

import { FC, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Monitor, Users, DoorOpen, PartyPopper, ArrowUpRight, Building2, Zap, Sparkles, Globe, type LucideIcon, CalendarDays, Palette, Star, Shield, Briefcase, Laptop, Rocket } from 'lucide-react';
import HotdeskButton from './HotdeskButton';
import styles from './WorkspaceTypesSection.module.scss';

/* ── Icon registry ── */
const ICON_MAP: Record<string, LucideIcon> = {
  Monitor,
  Users,
  DoorOpen,
  PartyPopper,
  Building2,
  Zap,
  Sparkles,
  Globe,
  CalendarDays,
  Palette,
  Star,
  Shield,
  Briefcase,
  Laptop,
  Rocket
};
const resolveIcon = (name?: string): LucideIcon => ICON_MAP[name || ''] || Monitor;

/* ── Types ── */
interface Tag {
  label: string;
  icon?: string;
}
interface WorkspaceType {
  icon: string;
  title: string;
  description: string;
  cta: string;
  ctaLink: string;
  image: string;
  tags: Tag[];
}
const S3_BASE = 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4';
export interface WorkspaceTypesSectionProps {}
const WorkspaceTypesSection: FC<WorkspaceTypesSectionProps> = () => {
  const t = useTranslations('workspaceTypes');
  const [activeIndex, setActiveIndex] = useState(0);
  const types: WorkspaceType[] = [{
    icon: 'Monitor',
    title: t('desks.title'),
    description: t('desks.description'),
    cta: t('desks.cta'),
    ctaLink: '/workspaces?typeIn=1',
    image: `${S3_BASE}/workspace-types-desks.png`,
    tags: [{
      label: t('tags.freelancers'),
      icon: 'Briefcase'
    }, {
      label: t('tags.remote'),
      icon: 'Laptop'
    }, {
      label: t('tags.startups'),
      icon: 'Rocket'
    }, {
      label: t('tags.teams'),
      icon: 'Users'
    }]
  }, {
    icon: 'Users',
    title: t('meetingRooms.title'),
    description: t('meetingRooms.description'),
    cta: t('meetingRooms.cta'),
    ctaLink: '/workspaces?typeIn=2',
    image: `${S3_BASE}/workspace-types-meeting-rooms.png`,
    tags: [{
      label: t('tags.teams'),
      icon: 'Users'
    }, {
      label: t('tags.companies'),
      icon: 'Building2'
    }, {
      label: t('tags.startups'),
      icon: 'Zap'
    }]
  }, {
    icon: 'DoorOpen',
    title: t('privateOffices.title'),
    description: t('privateOffices.description'),
    cta: t('privateOffices.cta'),
    ctaLink: '/workspaces?typeIn=3',
    image: `${S3_BASE}/workspace-types-private-offices.png`,
    tags: [{
      label: t('tags.companies'),
      icon: 'Building2'
    }, {
      label: t('tags.enterprises'),
      icon: 'Shield'
    }]
  }, {
    icon: 'PartyPopper',
    title: t('eventSpaces.title'),
    description: t('eventSpaces.description'),
    cta: t('eventSpaces.cta'),
    ctaLink: '/workspaces?typeIn=4',
    image: `${S3_BASE}/workspace-types-event-spaces.png`,
    tags: [{
      label: t('tags.organizers'),
      icon: 'CalendarDays'
    }, {
      label: t('tags.creatives'),
      icon: 'Palette'
    }, {
      label: t('tags.brands'),
      icon: 'Star'
    }, {
      label: t('tags.globalTeams'),
      icon: 'Globe'
    }]
  }, {
    icon: 'Monitor',
    title: t('virtualOffices.title'),
    description: t('virtualOffices.description'),
    cta: t('virtualOffices.cta'),
    ctaLink: '/workspaces?typeIn=5',
    image: `${S3_BASE}/workspace-types-virtual-offices.png`,
    tags: [{
      label: t('tags.organizers'),
      icon: 'CalendarDays'
    }, {
      label: t('tags.creatives'),
      icon: 'Palette'
    }, {
      label: t('tags.brands'),
      icon: 'Star'
    }, {
      label: t('tags.globalTeams'),
      icon: 'Globe'
    }]
  }];
  const activeType = types[activeIndex];
  if (!activeType) return null;
  return <section className={styles.section} data-sentry-component="WorkspaceTypesSection" data-sentry-source-file="WorkspaceTypesSection.tsx">
            <div className={styles.heading}>
                <span className={styles.subtitle}>{t('subtitle')}</span>
                <h2 className={styles.title}>{t('heading')}</h2>
            </div>

            <div className={styles.content}>
                <div className={styles.cardList}>
                    {types.map((type, index) => {
          const Icon = resolveIcon(type.icon);
          const isActive = index === activeIndex;
          const number = String(index + 1).padStart(2, '0');
          return <div key={index}>
                                <button type="button" className={`${styles.card} ${isActive ? styles.cardActive : styles.cardInactive}`} onClick={() => setActiveIndex(index)}>
                                    <div className={styles.cardHeader}>
                                        <div className={`${styles.iconCircle} ${isActive ? styles.iconCircleActive : styles.iconCircleInactive}`}>
                                            <Icon size={20} />
                                        </div>
                                        <div className={styles.cardHeaderText}>
                                            <span className={`${styles.cardNumber} ${isActive ? styles.cardNumberActive : styles.cardNumberInactive}`}>
                                                {number}
                                            </span>
                                            <span className={`${styles.cardTitle} ${isActive ? styles.cardTitleActive : styles.cardTitleInactive}`}>
                                                {type.title}
                                            </span>
                                        </div>
                                        <div className={`${styles.arrowIcon} ${isActive ? styles.arrowIconActive : styles.arrowIconInactive}`}>
                                            <ArrowUpRight size={18} />
                                        </div>
                                    </div>

                                    {isActive && <div className={styles.cardBody}>
                                            <div className={styles.separator} />
                                            <p className={styles.description}>{type.description}</p>
                                            <HotdeskButton as="next-link" href={type.ctaLink} variant="secondary" size="sm" className={styles.ctaButton}>
                                                {type.cta}
                                            </HotdeskButton>
                                        </div>}
                                </button>
                                <div className={`${styles.imageArea}  ${isActive ? styles.mobileImageAreaOpen : styles.mobileImageArea} d-block d-md-none`}>
                                         {types.map((type, index) => <div key={index} className={`${index === activeIndex ? styles.visible : styles.hidden}`}>
                                                <Image src={type.image} alt={type.title} fill className={styles.image} sizes="100vw" priority={index === 0} />
                                            </div>)}
                                        <div className={styles.tagBadges}>
                                            {activeType.tags.map((tag, i) => {
                  const TagIcon = resolveIcon(tag.icon);
                  return <span key={i} className={styles.tagBadge}>
                                                        <TagIcon size={14} />
                                                        {tag.label}
                                                    </span>;
                })}
                                        </div>
                                        <div className={styles.labelPill}>
                                            {activeType.title}
                                        </div>
                                    </div>
                                {/* )} */}
                            </div>;
        })}
                </div>

                <div className={`${styles.imageArea} d-none d-md-block`}>
                    {types.map((type, index) => <Image key={index} src={type.image} alt={type.title} fill className={styles.image} sizes="816px" priority={index === 0} style={{
          opacity: index === activeIndex ? 1 : 0,
          transition: 'opacity 0.4s ease-in-out'
        }} />)}
                    <div className={styles.tagBadges}>
                        {activeType.tags.map((tag, i) => {
            const TagIcon = resolveIcon(tag.icon);
            return <span key={i} className={styles.tagBadge}>
                                    <TagIcon size={14} />
                                    {tag.label}
                                </span>;
          })}
                    </div>
                    <div className={styles.labelPill}>
                        {activeType.title}
                    </div>
                </div>
            </div>
        </section>;
};
export default WorkspaceTypesSection;